import { useMemo, useState } from 'react';
import { AsyncBoundary, Drawer, Notice, useToast } from '@kollez/design-system';
import {
  useGrievances, useUpdateGrievance,
  type GrievanceCategory, type GrievancePriority, type GrievanceStatus,
  type GrievanceRow,
} from '@/lib/queries';

/**
 * /tpo/grievances (Page 81/N) — DPDP §13 review queue for reviewers.
 *
 * Endpoint auto-scopes to tenant when caller is a reviewer role. This
 * page renders the queue with the 90-day SLA in view, and lets TPO/
 * principal/group-admin acknowledge, resolve, escalate or close.
 */

const CATEGORY_LABEL: Record<GrievanceCategory, string> = {
  unauthorized_processing: 'Unauthorised processing',
  consent_violation: 'Consent violation',
  data_leak: 'Data leak',
  ai_output_error: 'AI output error',
  discrimination: 'Discrimination',
  other: 'Other',
};

const STATUS_LABEL: Record<GrievanceStatus, string> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  in_review: 'In review',
  resolved: 'Resolved',
  escalated: 'Escalated',
  closed: 'Closed',
};

const PRIORITY_LABEL: Record<GrievancePriority, string> = {
  low: 'Low', normal: 'Normal', high: 'High', urgent: 'Urgent',
};

function statusTone(s: GrievanceStatus): string {
  if (s === 'open') return 'badge-warning';
  if (s === 'resolved' || s === 'closed') return 'badge-success';
  if (s === 'escalated') return 'badge-danger';
  return 'badge-info';
}

function priorityTone(p: GrievancePriority): string {
  if (p === 'urgent') return 'badge-danger';
  if (p === 'high') return 'badge-warning';
  return '';
}

function fmt(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function daysLeft(iso?: string): { text: string; breached: boolean } {
  if (!iso) return { text: '—', breached: false };
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return { text: '—', breached: false };
  const days = Math.ceil((then - Date.now()) / (24 * 60 * 60 * 1000));
  if (days < 0) return { text: `${-days}d overdue`, breached: true };
  if (days === 0) return { text: 'due today', breached: false };
  return { text: `${days}d left`, breached: false };
}

type Decision = 'acknowledged' | 'in_review' | 'resolved' | 'escalated' | 'closed';

export function TpoGrievancesPage() {
  const toast = useToast();
  const [status, setStatus] = useState<GrievanceStatus | ''>('open');
  const [priority, setPriority] = useState<GrievancePriority | ''>('');
  const [category, setCategory] = useState<GrievanceCategory | ''>('');
  const [slaOnly, setSlaOnly] = useState(false);
  const [q, setQ] = useState('');

  const list = useGrievances({ status, priority, category, slaBreached: slaOnly || undefined, q, sortBy: 'slaDeadline', sortDir: 'asc', limit: 100 });
  const rows = list.data?.rows ?? [];
  const summary = list.data?.summary ?? {};

  const [openRow, setOpenRow] = useState<GrievanceRow | null>(null);
  const [decision, setDecision] = useState<Decision>('acknowledged');
  const [note, setNote] = useState('');
  const [escalateTo, setEscalateTo] = useState<'platform_dpo' | 'tenant_dpo' | 'external_regulator'>('tenant_dpo');

  const update = useUpdateGrievance(openRow?._id ?? '');
  const canSubmit = useMemo(() => Boolean(openRow) && note.trim().length >= 5, [openRow, note]);

  const closeDrawer = () => { setOpenRow(null); setNote(''); setDecision('acknowledged'); };

  const submit = () => {
    if (!openRow) return;
    update.mutate(
      {
        status: decision,
        note: note.trim(),
        ...(decision === 'escalated' ? { escalatedTo: escalateTo } : {}),
      },
      {
        onSuccess: () => { toast.show({ message: `Marked ${STATUS_LABEL[decision].toLowerCase()}.` }); closeDrawer(); },
        onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
      },
    );
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">DPDP · §13 · 90-day SLA</p>
          <h1 className="h-page">Grievances</h1>
          <p className="subhead">
            Every grievance filed by a student at this college. Acknowledge quickly,
            resolve with a written note, and escalate to the DPO / regulator when the
            grievance is not within your authority to fix.
          </p>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <div className="card kpi"><span className="kpi-label">Open</span>
          <span className="kpi-value">{summary.open ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">In review</span>
          <span className="kpi-value">{summary.in_review ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">Escalated</span>
          <span className="kpi-value">{summary.escalated ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">Resolved</span>
          <span className="kpi-value">{summary.resolved ?? 0}</span></div>
      </div>

      <div className="card">
        <div className="filterbar">
          <input className="input" placeholder="Search subject or description" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as GrievanceStatus | '')}>
            <option value="">Any status</option>
            {(Object.entries(STATUS_LABEL) as Array<[GrievanceStatus, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select className="select" value={priority} onChange={(e) => setPriority(e.target.value as GrievancePriority | '')}>
            <option value="">Any priority</option>
            {(Object.entries(PRIORITY_LABEL) as Array<[GrievancePriority, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select className="select" value={category} onChange={(e) => setCategory(e.target.value as GrievanceCategory | '')}>
            <option value="">Any category</option>
            {(Object.entries(CATEGORY_LABEL) as Array<[GrievanceCategory, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={slaOnly} onChange={(e) => setSlaOnly(e.target.checked)} />
            <span>SLA breached only</span>
          </label>
          <span className="fb-count">{list.data?.meta?.total ?? rows.length}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle="No grievances in scope"
          emptyHint="Widen the filters, or celebrate quietly — nothing is on your DPDP desk right now."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Filed</th>
                  <th>Student</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>SLA</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const sla = daysLeft(r.slaDeadline);
                  return (
                    <tr key={r._id} onClick={() => setOpenRow(r)} style={{ cursor: 'pointer' }}>
                      <td className="cell-sub">{fmt(r.createdAt)}</td>
                      <td>
                        <div className="cell-title">
                          {typeof r.user === 'object' && r.user?.fullName ? r.user.fullName : '—'}
                        </div>
                        {typeof r.user === 'object' && r.user?.email && (
                          <div className="cell-sub">{r.user.email}</div>
                        )}
                      </td>
                      <td className="cell-title">{r.subject}</td>
                      <td className="cell-sub">{CATEGORY_LABEL[r.category]}</td>
                      <td><span className={`badge ${priorityTone(r.priority)}`}>{PRIORITY_LABEL[r.priority]}</span></td>
                      <td className={sla.breached ? 'cell-danger' : 'cell-sub'}>{sla.text}</td>
                      <td><span className={`badge ${statusTone(r.status)}`}>{STATUS_LABEL[r.status]}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Notice tone="info">
        <b>Escalation ladder.</b> Escalate to the tenant DPO when you don't have the authority
        to fix, to the platform DPO when Kollez itself is involved, and to the external
        regulator only when the internal channels have been exhausted. Every step is audited.
      </Notice>

      <Drawer open={Boolean(openRow)} onClose={closeDrawer} title={openRow?.subject ?? 'Grievance'} size="lg">
        {openRow && (
          <div className="stack">
            <div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className={`badge ${statusTone(openRow.status)}`}>{STATUS_LABEL[openRow.status]}</span>
                <span className={`badge ${priorityTone(openRow.priority)}`}>{PRIORITY_LABEL[openRow.priority]}</span>
                <span className="cell-sub">{CATEGORY_LABEL[openRow.category]}</span>
              </div>
              <p className="cell-sub m-0 mt-1">
                Filed {fmt(openRow.createdAt)}
                {typeof openRow.user === 'object' && openRow.user?.fullName && ` by ${openRow.user.fullName}`}
              </p>
              <p className="cell-sub m-0">SLA deadline: {fmt(openRow.slaDeadline)} · {daysLeft(openRow.slaDeadline).text}</p>
            </div>

            <div className="card card-pad">
              <div className="eyebrow">Description</div>
              <p className="m-0 mt-1 whitespace-pre-line">{openRow.description}</p>
            </div>

            {openRow.evidenceUrls && openRow.evidenceUrls.length > 0 && (
              <div className="card card-pad">
                <div className="eyebrow mb-2">Evidence</div>
                <ul className="grid gap-1 text-sm">
                  {openRow.evidenceUrls.map((u) => (
                    <li key={u}><a className="link" href={u} target="_blank" rel="noreferrer">{u}</a></li>
                  ))}
                </ul>
              </div>
            )}

            {openRow.resolutionNote && (
              <div className="card card-pad">
                <div className="eyebrow">Prior reviewer note</div>
                <p className="m-0 mt-1 whitespace-pre-line">{openRow.resolutionNote}</p>
              </div>
            )}

            <div className="card card-pad">
              <div className="eyebrow mb-2">Take action</div>
              <div className="grid gap-2 grid-cols-2 lg:grid-cols-5">
                {(['acknowledged', 'in_review', 'resolved', 'escalated', 'closed'] as Decision[]).map((d) => (
                  <label key={d} className={`card card-pad cursor-pointer ${decision === d ? 'border-primary' : ''}`}>
                    <input type="radio" name="decision" value={d} checked={decision === d} onChange={() => setDecision(d)} className="mr-2" />
                    <b>{STATUS_LABEL[d]}</b>
                  </label>
                ))}
              </div>
              {decision === 'escalated' && (
                <label className="block mt-3">
                  <span className="eyebrow">Escalate to</span>
                  <select className="select mt-1" value={escalateTo} onChange={(e) => setEscalateTo(e.target.value as typeof escalateTo)}>
                    <option value="tenant_dpo">Tenant DPO</option>
                    <option value="platform_dpo">Platform DPO</option>
                    <option value="external_regulator">External regulator</option>
                  </select>
                </label>
              )}
              <label className="block mt-3">
                <span className="eyebrow">Note (5-2000 chars)</span>
                <textarea
                  className="input mt-1"
                  rows={4}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="What did you do, or what does the student need to know?"
                />
              </label>
              <div className="flex justify-end gap-2 mt-3">
                <button className="btn btn-ghost" onClick={closeDrawer}>Cancel</button>
                <button className="btn btn-primary" disabled={!canSubmit || update.isPending} onClick={submit}>
                  {update.isPending ? 'Saving…' : 'Save decision'}
                </button>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
